import {
  IonHeader,
  IonIcon,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonToolbar,
  type RefresherEventDetail,
  useIonViewWillEnter,
} from "@ionic/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { searchOutline } from "ionicons/icons";
import { calendarOutline } from "ionicons/icons";
import { DateHeader } from "@/components/atoms/date-header";
import { SearchField } from "@/components/atoms/search-field";
import { Content } from "@/components/content";
import { EmptyState } from "@/components/molecules/empty-state";
import { OrganizationDropdown } from "@/components/molecules/organization/organization-dropdown";
import { ProjectCardCarousel } from "@/components/molecules/project/project-card.featured.carousel";
import { ProjectCardUpcoming } from "@/components/molecules/project/project-card.upcoming";
import { Toolbar } from "@/components/toolbar";
import { api } from "@/lib/backend";
import { type Project, type ProjectEmployeeDayCard } from "@/lib/backend/api";
import { queryOptions_projects_employee_day_range } from "@/lib/requests/project/project.employee-day-range";
import { dayKeyToDate, startOfDayInTz, toDayKey } from "@/utils/format-datetime";

/**
 * Begin van vandaag in projecttijd, niet op de klok van het toestel.
 *
 * Staat bewust op moduleniveau zodat de filterwaarde stabiel blijft tussen renders — dat is
 * bestaand gedrag, alleen de tijdzone klopt nu.
 */
const today = startOfDayInTz().toISOString();

export function ProjectsPage() {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // Helper to get next 12 months date range for featured projects
  const getNext12Months = (): Date => {
    const next12Months = new Date();
    next12Months.setMonth(next12Months.getMonth() + 12);
    next12Months.setHours(23, 59, 59, 999);
    return next12Months;
  };

  const featuredFilter = useMemo(
    () => [
      {
        AND: [
          {
            startDate: {
              operator: "gte" as const,
              value: today,
            },
          },
          {
            startDate: {
              operator: "lte" as const,
              value: getNext12Months().toISOString(),
            },
          },
          {
            isFeatured: {
              operator: "equals" as const,
              value: true,
            },
          },
          ...(searchText
            ? [
                {
                  name: {
                    operator: "contains" as const,
                    value: searchText,
                  },
                },
              ]
            : []),
        ],
      },
    ],
    [searchText],
  );

  const { data: featuredProjectsData, refetch: refetchFeatured } = useQuery({
    ...queryOptions_projects_employee_day_range({
      filter: featuredFilter,
      sort: {
        field: "startDate",
        order: "asc",
      },
      pagination: {
        take: 50,
        page: 1,
      },
    }),
  });

  // Helper to get week start (Monday) in NL timezone
  //
  // De oude versie beloofde NL maar rekende met `getDay()`, `getDate()` en
  // `dayStart()` — allemaal velden van de klok van het TOESTEL. De week begon
  // daardoor op de maandag van de telefoon, niet die van het project.
  const getWeekStartNL = (date: Date): Date => {
    const dayInTz = dayKeyToDate(toDayKey(date));
    const weekday = dayInTz.getUTCDay();
    // Zondag telt als laatste dag van de week, niet als eerste.
    dayInTz.setUTCDate(dayInTz.getUTCDate() - (weekday === 0 ? 6 : weekday - 1));

    return startOfDayInTz(dayInTz);
  };

  // // Helper to get week end (Sunday) in NL timezone
  // const getWeekEndNL = (date: Date): Date => {
  //   const weekStart = getWeekStartNL(date);
  //   const weekEnd = new Date(weekStart);
  //   weekEnd.setDate(weekStart.getDate() + 6); // Add 6 days to get Sunday
  //   weekEnd.setHours(23, 59, 59, 999); // End of day
  //   return weekEnd;
  // };

  const getNext2Years = (): Date => {
    const next2Years = new Date();
    next2Years.setFullYear(next2Years.getFullYear() + 2);
    next2Years.setHours(23, 59, 59, 999);
    return next2Years;
  };

  const upcomingFilter = useMemo(
    () => [
      {
        AND: [
          {
            startDate: {
              operator: "gte" as const,
              value: getWeekStartNL(new Date()).toISOString(),
            },
          },
          {
            startDate: {
              operator: "lte" as const,
              value: getNext2Years().toISOString(),
            },
          },
          ...(searchText
            ? [
                {
                  name: {
                    operator: "contains" as const,
                    value: searchText,
                  },
                },
              ]
            : []),
        ],
      },
    ],
    [searchText],
  );

  const { data: projectsData, refetch: refetchProjects } = useQuery({
    ...queryOptions_projects_employee_day_range({
      filter: upcomingFilter,
      sort: {
        field: "startDate",
        order: "asc",
      },
      pagination: {
        take: 1000,
        page: 1,
      },
    }),
  });

  const upcomingCards = projectsData?.items.filter((item) => item.canApply) ?? [];

  // Transform featured cards to projects for the carousel
  // Group by projectId and take the first occurrence to get project details
  const featuredProjects = useMemo(() => {
    const cards = featuredProjectsData?.items ?? [];
    // Filter by canApply first
    const applicableCards = cards.filter((card) => card.canApply);
    const projectMap = new Map<string, ProjectEmployeeDayCard>();

    // Group by projectId, keeping only the first (earliest) card per project
    applicableCards.forEach((card) => {
      if (!projectMap.has(card.projectId)) {
        projectMap.set(card.projectId, card);
      }
    });

    // Transform to Project objects
    return Array.from(projectMap.values()).map(
      (card): Project => ({
        id: card.projectId,
        name: card.projectName,
        image: card.projectImage,
        startDate: new Date(card.day).toISOString(),
        status: card.projectStatus,
        stats: {
          uniqueUserFunctions: card.availableFunctions,
          totalCapacity: 0,
          totalApplications: 0,
          totalShifts: 0,
          filledShifts: 0,
        },
        // Add other required Project fields with defaults
        endDate: null,
        description: null,
        isArchived: false,
        isPublished: card.isPublished,
        isFeatured: true, // We know this is true because we filtered for it
        createdAt: "",
        updatedAt: "",
        schedulingMethod: "",
        signupIsScheduling: false,
        allowEmployeeSubmitHours: false,
        allowEmployeeSubmitDeclarations: false,
        enableQRCodeClocking: false,
        timeZone: null,
      }),
    );
  }, [featuredProjectsData?.items]);

  // Group all cards by day
  const groupedProjects = useMemo(() => {
    // Group by day
    const groups: Record<string, ProjectEmployeeDayCard[]> = {};

    upcomingCards.forEach((card) => {
      const cardDay = card.day.split("T")[0]; // Extract YYYY-MM-DD from ISO string
      if (!groups[cardDay]) {
        groups[cardDay] = [];
      }
      groups[cardDay].push(card);
    });

    // Sort dates and map to expected structure
    return Object.keys(groups)
      .sort()
      .map((dateKey) => ({
        // `new Date("2026-08-21")` is middernacht UTC: westelijk van Greenwich
        // valt dat terug op de 20e, en dan staat er "donderdag 20 augustus"
        // boven de diensten van vrijdag de 21e.
        date: dayKeyToDate(dateKey),
        items: groups[dateKey],
      }));
  }, [upcomingCards]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await Promise.all([refetchFeatured(), refetchProjects()]);
    event.detail.complete();
  };

  // Refetch projects when navigating back to this page
  useIonViewWillEnter(() => {
    void refetchFeatured();
    void refetchProjects();
  });

  return (
    <IonPage>
      <IonHeader>
        <Toolbar className="ion-no-border plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),theme(spacing.4))]">
          {isSearchExpanded ? (
            <SearchField
              value={searchText}
              onChange={setSearchText}
              onBlur={() => {
                if (!searchText) {
                  setIsSearchExpanded(false);
                }
              }}
              placeholder={t("common:search.placeholder")}
              autoFocus
              debounce={300}
            />
          ) : (
            <div className="flex items-center gap-1">
              {/* Dit scherm had als enige tabblad geen titel in de balk -- je zag alleen een logo en
                  een vergrootglas. Nu staat hij er, op dezelfde regel als de knoppen: dat kost geen
                  hoogte (de rij is al zo hoog als de knoppen) en het maakt de balk gelijk aan
                  Diensten en Chat. */}
              <h1 className="text-2xl font-semibold leading-[1.2] text-txt-primary">
                {t("routes:tabs.discover")}
              </h1>

              {/* Search Button */}
              <button
                type="button"
                onClick={() => setIsSearchExpanded(true)}
                className="touch-target ml-auto rounded-lg p-2 transition-opacity active:opacity-70"
                aria-label={t("common:search.label")}
              >
                <IonIcon icon={searchOutline} className="text-lg text-txt-primary" />
              </button>

              {/* Organization Logo / Dropdown */}
              <OrganizationDropdown />
            </div>
          )}
        </Toolbar>
      </IonHeader>
      <Content scrollUnderChrome>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>

        {/* Featured projects section */}
        {featuredProjects.length > 0 && (
          <>
            <IonHeader collapse="condense" className="ion-no-border !ion-mt-0 !ion-pt-0">
              <IonToolbar className="!ion-mt-0 !ion-pt-0">
                <div slot="start" className="text-xl font-semibold ion-text-txt-primary">
                  {t("common:featured.title")}
                </div>
              </IonToolbar>
            </IonHeader>

            <IonList className="flex flex-row gap-4 !ion-mt-0 !ion-pt-0">
              <ProjectCardCarousel projects={featuredProjects} />
            </IonList>
          </>
        )}

        {/* Grouped upcoming projects */}
        {groupedProjects.length > 0 ? (
          groupedProjects.map((group) => (
            <div key={group.date.toISOString()}>
              <DateHeader date={group.date} />
              <IonList className="flex flex-col gap-4">
                {group.items.map((card, index) => (
                  <ProjectCardUpcoming key={`${card.projectId}-${card.day}-${index}`} card={card} />
                ))}
              </IonList>
            </div>
          ))
        ) : (
          <EmptyState
            icon={calendarOutline}
            title={t("routes:projects.empty-title")}
            description={
              searchText
                ? t("routes:projects.empty-search")
                : t("routes:projects.empty-description")
            }
          />
        )}
      </Content>
    </IonPage>
  );
}
